'use client';

import { ChevronDown, Settings, LogOut, Zap } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { signOut } from 'next-auth/react';
import { Session } from 'next-auth';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UsageDisplay } from '../components/usage-display';

// Authenticated navigation for logged-in users
export function AihumanitAuthenticatedNavigation({ session }: { session: Session | null }) {

    const getInitials = (name?: string | null, email?: string | null) => {
        if (name) {
            return name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);
        }
        if (email) {
            return email.slice(0, 2).toUpperCase();
        }
        return 'U';
    };

    return (
        <nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo */}
                    <div className="flex items-center">
                        <Link href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
                            <Image
                                src="/aihumanit_logo.png"
                                alt="AI Humanit logo"
                                width={48}
                                height={48}
                                className="w-12 h-12 rounded-2xl"
                                priority
                            />
                            <span className="font-semibold text-2xl text-gray-800">AI Humanit</span>
                        </Link>
                    </div>

                    {/* Right side - User menu */}
                    <div className="flex items-center space-x-4">

                        {/* User dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <div className="flex items-center space-x-2 p-2 cursor-pointer hover:bg-gray-50 rounded-md transition-colors">
                                    <Avatar className="h-8 w-8">
                                        <AvatarImage src={session?.user?.image || ''} />
                                        <AvatarFallback className="bg-purple-100 text-purple-600">
                                            {getInitials(session?.user?.name, session?.user?.email)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="hidden md:flex md:flex-col md:items-start">
                                        <span className="text-sm font-medium text-gray-700">
                                            {session?.user?.name || session?.user?.email}
                                        </span>
                                        {session && (
                                            <div className="mt-0.5">
                                                <UsageDisplay session={session} />
                                            </div>
                                        )}
                                    </div>
                                    <ChevronDown className="h-4 w-4 text-gray-500" />
                                </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem asChild>
                                    <Link href="/subscription" className="flex items-center justify-between cursor-pointer">
                                        <div className="flex items-center">
                                            <Zap className="mr-2 h-4 w-4" />
                                            <span>Subscription</span>
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {session && <UsageDisplay session={session} textOnly />}
                                        </div>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild>
                                    <Link href="/settings" className="flex items-center">
                                        <Settings className="mr-2 h-4 w-4" />
                                        <span>Settings</span>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => signOut({ callbackUrl: '/aihumanit' })}
                                    className="text-red-600 focus:text-red-600"
                                >
                                    <LogOut className="mr-2 h-4 w-4" />
                                    <span>Sign out</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </div>
        </nav>
    );
}
